import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectOrderEntryByIdFromBlotter,
  selectTakeProfitErrors,
  selectTradeIdFromTakeProfitTile,
} from '@/store/orders/takeProfitOrder/selectors.ts';

type TakeProfitCustomerPriceProps = {
  customerPrice: number | undefined;
  setCustomerPrice: (value: number | undefined) => void;
  setLimitPrice: (value: number | undefined) => void;
};

export const TakeProfitEditingCustomerPrice = (props: TakeProfitCustomerPriceProps) => {
  const { customerPrice, setCustomerPrice, setLimitPrice } = props;
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromTakeProfitTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const userContextValue = useUserContext();
  const isInternalSales = userContextValue?.currentUser.userType === 'Internal Sales';

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const customerPriceError = errors?.customerPrice;

  const validateTakeProfit = useValidateTakeProfit();

  const handleCustomerPriceChange = async (value: number | undefined) => {
    track('Order action', 'Customer price changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateTakeProfit({
        customerPrice: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setCustomerPrice(value);

        if (isDefined(response?.data?.limitPrice)) {
          setLimitPrice(response.data.limitPrice);
        }
      }
    }
  };

  return (
    <OrderRow labelId={`order.fields.${isInternalSales ? 'customerPrice' : 'limitPrice'}.label`}>
      <OmsFieldErrorWrapper errors={customerPriceError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          maxDecimals={9}
          value={
            customerPrice ??
            (isDefined(orderFromBlotter?.customerPrice) ? (orderFromBlotter?.customerPrice as number) : undefined)
          }
          onChange={handleCustomerPriceChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          tabNoIndex
          data-e2e="customer-price"
          size="sm"
          state={isDefined(customerPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
